import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getComponent } from "../data/catalog";
import { validatePlacement } from "../domain/placement";
import { findRackSettings, getPlacementLocalDisplayName, getRackDisplayName } from "../domain/rack-settings";
import type { MapDefinition, Placement, RackDefinition, RackSettings } from "../domain/types";
import { useUiLanguage } from "../i18n";
import { getDragSession, setDragSession } from "./drag-session";
import { EditIcon, LockIcon, SettingsIcon, TrashIcon } from "./icons";

const ROW_HEIGHT = 36;
const ACTION_POPOVER_WIDTH = 240;
const ACTION_POPOVER_HEIGHT = 150;

interface ComponentSelection {
    readonly placementId: string;
    readonly anchorLeft: number;
    readonly anchorTop: number;
    readonly anchorHeight: number;
}

interface MarqueeSelection {
    readonly pointerId: number;
    readonly currentClientX: number;
    readonly currentClientY: number;
    readonly startX: number;
    readonly startY: number;
    readonly currentX: number;
    readonly currentY: number;
    readonly baseSelection: readonly string[];
}

interface ClipboardPlacement {
    readonly componentId: string;
    readonly relativeStartU: number;
    readonly localName?: string;
}

interface PlacementDraft {
    readonly componentId: string;
    readonly rackId: string;
    readonly startU: number;
    readonly localName?: string;
}

interface MoveDraft {
    readonly placementId: string;
    readonly rackId: string;
    readonly startU: number;
}

interface DropPreview {
    readonly blocks: readonly {
        readonly placementId?: string;
        readonly startU: number;
        readonly sizeU: number;
    }[];
    readonly valid: boolean;
    readonly reason?: string;
}

interface RackDesignerProps {
    readonly map: MapDefinition;
    readonly rack: RackDefinition;
    readonly placements: readonly Placement[];
    readonly rackSettings: readonly RackSettings[];
    readonly onPlace: (componentId: string, rackId: string, startU: number) => void;
    readonly onPaste: (placements: readonly PlacementDraft[]) => void;
    readonly onMove: (placementId: string, rackId: string, startU: number) => void;
    readonly onMoveMany: (placements: readonly MoveDraft[]) => void;
    readonly onRename: (placement: Placement) => void;
    readonly onEditRack: () => void;
    readonly onDelete: (placementId: string) => void;
    readonly onDeleteSelectionRequest: (placementIds: readonly string[]) => void;
}

function getBlockPosition(totalU: number, startU: number, sizeU: number, rowHeight: number): React.CSSProperties {
    return {
        top: (totalU - startU - sizeU + 1) * rowHeight,
        height: sizeU * rowHeight,
    };
}

function findPasteDrafts(
    map: MapDefinition,
    rack: RackDefinition,
    placements: readonly Placement[],
    clipboard: readonly ClipboardPlacement[],
): readonly PlacementDraft[] | null {
    for (let baseStartU = 1; baseStartU <= rack.sizeU; baseStartU += 1) {
        const provisional = [...placements];
        const drafts: PlacementDraft[] = [];
        let isValid = true;
        for (const [index, item] of clipboard.entries()) {
            const startU = baseStartU + item.relativeStartU;
            const validation = validatePlacement(map, provisional, item.componentId, rack.id, startU);
            if (!validation.valid) {
                isValid = false;
                break;
            }
            const draft: PlacementDraft = {
                componentId: item.componentId,
                rackId: rack.id,
                startU,
                ...(item.localName === undefined ? {} : { localName: item.localName }),
            };
            drafts.push(draft);
            provisional.push({
                id: `paste-preview-${baseStartU}-${index}`,
                ...draft,
            });
        }
        if (isValid) {
            return drafts;
        }
    }
    return null;
}

function isTextEntryTarget(target: EventTarget | null): boolean {
    return (
        target instanceof HTMLElement &&
        (target.isContentEditable || target.matches("input, textarea, select, [role='textbox']"))
    );
}

export function RackDesigner({
    map,
    rack,
    placements,
    rackSettings,
    onPlace,
    onPaste,
    onMove,
    onMoveMany,
    onRename,
    onEditRack,
    onDelete,
    onDeleteSelectionRequest,
}: RackDesignerProps): React.JSX.Element {
    const { entity, runtimeText, t } = useUiLanguage();
    const [showComponentLabels, setShowComponentLabels] = useState(true);
    const [showColorOverlay, setShowColorOverlay] = useState(false);
    const rowHeight = ROW_HEIGHT;
    const [preview, setPreview] = useState<DropPreview | null>(null);
    const [actionSelection, setActionSelection] = useState<ComponentSelection | null>(null);
    const [selectedPlacementIds, setSelectedPlacementIds] = useState<readonly string[]>([]);
    const [marquee, setMarquee] = useState<MarqueeSelection | null>(null);
    const rackFrameRef = useRef<HTMLDivElement>(null);
    const [clipboard, setClipboard] = useState<readonly ClipboardPlacement[]>([]);
    const [shortcutStatus, setShortcutStatus] = useState<string | null>(null);
    const rackPlacements = useMemo(
        () => placements.filter((placement) => placement.rackId === rack.id),
        [placements, rack.id],
    );
    const units = Array.from({ length: rack.sizeU }, (_, index) => rack.sizeU - index);
    const selected = rackPlacements.find((placement) => placement.id === actionSelection?.placementId);
    const selectedDefinition = selected === undefined ? undefined : getComponent(selected.componentId);
    const rackSetting = findRackSettings(rackSettings, rack.id);
    const rackName = getRackDisplayName(rack, rackSettings, entity("rack", rack.id, rack.name));
    const showRackNameAsComponentPrefix = rackSetting?.showNameAsComponentPrefix === true;
    const mapName = entity("map", map.id, map.name);

    useEffect(() => {
        setActionSelection(null);
        setSelectedPlacementIds([]);
        setMarquee(null);
        setShortcutStatus(null);
    }, [rack.id]);

    useEffect(() => {
        const availablePlacementIds = new Set(rackPlacements.map((placement) => placement.id));
        setSelectedPlacementIds((current) => {
            const available = current.filter((placementId) => availablePlacementIds.has(placementId));
            return available.length === current.length ? current : available;
        });
        setActionSelection((current) =>
            current !== null && !availablePlacementIds.has(current.placementId) ? null : current,
        );
    }, [rackPlacements]);

    useEffect(() => {
        function handleShortcut(event: KeyboardEvent): void {
            if (isTextEntryTarget(event.target)) {
                return;
            }
            const key = event.key.toLowerCase();
            if (key === "delete" && selectedPlacementIds.length > 0) {
                onDeleteSelectionRequest(selectedPlacementIds);
                event.preventDefault();
                return;
            }
            if ((!event.ctrlKey && !event.metaKey) || event.altKey) {
                return;
            }
            if (key === "c" && selectedPlacementIds.length > 0) {
                const selectedPlacements = rackPlacements
                    .filter((placement) => selectedPlacementIds.includes(placement.id))
                    .sort((left, right) => left.startU - right.startU);
                const firstStartU = selectedPlacements[0]?.startU;
                if (firstStartU === undefined) {
                    return;
                }
                setClipboard(
                    selectedPlacements.map((placement) => ({
                        componentId: placement.componentId,
                        relativeStartU: placement.startU - firstStartU,
                        ...(placement.localName === undefined
                            ? {}
                            : { localName: `${placement.localName} copy`.slice(0, 80) }),
                    })),
                );
                setShortcutStatus(`${selectedPlacements.length} ${t("copied")}`);
                event.preventDefault();
                return;
            }
            if (key !== "v" || clipboard.length === 0) {
                return;
            }
            const drafts = findPasteDrafts(map, rack, placements, clipboard);
            if (drafts === null) {
                setShortcutStatus(t("pasteUnavailable"));
                event.preventDefault();
                return;
            }
            onPaste(drafts);
            setSelectedPlacementIds([]);
            setActionSelection(null);
            setShortcutStatus(`${drafts.length} ${t("pasted")}`);
            event.preventDefault();
        }

        window.addEventListener("keydown", handleShortcut);
        return () => window.removeEventListener("keydown", handleShortcut);
    }, [clipboard, map, onDeleteSelectionRequest, onPaste, placements, rack, rackPlacements, selectedPlacementIds, t]);

    function beginMarquee(event: React.PointerEvent<HTMLDivElement>): void {
        if (event.button !== 0 || event.pointerType !== "mouse") {
            return;
        }
        const target = event.target;
        if (target instanceof Element && target.closest(".rack-component") !== null) {
            return;
        }
        const bounds = event.currentTarget.getBoundingClientRect();
        const baseSelection = event.ctrlKey || event.metaKey ? selectedPlacementIds : [];
        setSelectedPlacementIds(baseSelection);
        setActionSelection(null);
        setShortcutStatus(null);
        setMarquee({
            pointerId: event.pointerId,
            currentClientX: event.clientX,
            currentClientY: event.clientY,
            startX: event.clientX - bounds.left,
            startY: event.clientY - bounds.top,
            currentX: event.clientX - bounds.left,
            currentY: event.clientY - bounds.top,
            baseSelection,
        });
        event.currentTarget.setPointerCapture(event.pointerId);
        event.preventDefault();
    }

    function updateMarqueeSelection(
        current: MarqueeSelection,
        frame: HTMLDivElement,
        clientX: number,
        clientY: number,
    ): void {
        const bounds = frame.getBoundingClientRect();
        const next = {
            ...current,
            currentClientX: clientX,
            currentClientY: clientY,
            currentX: Math.max(0, Math.min(bounds.width, clientX - bounds.left)),
            currentY: Math.max(0, Math.min(bounds.height, clientY - bounds.top)),
        };
        const selectionBounds = {
            left: Math.min(next.startX, next.currentX),
            right: Math.max(next.startX, next.currentX),
            top: Math.min(next.startY, next.currentY),
            bottom: Math.max(next.startY, next.currentY),
        };
        const hits = Array.from(frame.querySelectorAll<HTMLElement>("[data-placement-id]"))
            .filter((element) => {
                const componentBounds = element.getBoundingClientRect();
                return (
                    componentBounds.left - bounds.left < selectionBounds.right &&
                    componentBounds.right - bounds.left > selectionBounds.left &&
                    componentBounds.top - bounds.top < selectionBounds.bottom &&
                    componentBounds.bottom - bounds.top > selectionBounds.top
                );
            })
            .map((element) => element.dataset.placementId)
            .filter((placementId): placementId is string => placementId !== undefined);
        setSelectedPlacementIds([...new Set([...next.baseSelection, ...hits])]);
        setMarquee(next);
    }

    function updateMarquee(event: React.PointerEvent<HTMLDivElement>): void {
        if (marquee === null || marquee.pointerId !== event.pointerId) {
            return;
        }
        updateMarqueeSelection(marquee, event.currentTarget, event.clientX, event.clientY);
    }

    function updateMarqueeAfterScroll(): void {
        const frame = rackFrameRef.current;
        if (marquee === null || frame === null) {
            return;
        }
        updateMarqueeSelection(marquee, frame, marquee.currentClientX, marquee.currentClientY);
    }

    function finishMarquee(event: React.PointerEvent<HTMLDivElement>): void {
        if (marquee === null || marquee.pointerId !== event.pointerId) {
            return;
        }
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        setMarquee(null);
    }

    function updatePreview(event: React.DragEvent<HTMLDivElement>): void {
        event.preventDefault();
        const session = getDragSession();
        if (session === null) {
            return;
        }
        const bounds = event.currentTarget.getBoundingClientRect();
        const rowFromTop = Math.max(0, Math.min(rack.sizeU - 1, Math.floor((event.clientY - bounds.top) / rowHeight)));
        const anchorSizeU = session.kind === "placement-group" ? session.anchorSizeU : session.sizeU;
        const anchorStartU = rack.sizeU - rowFromTop - anchorSizeU + 1;
        if (session.kind === "placement-group") {
            const movingIds = new Set(session.items.map((item) => item.placementId));
            const provisional = placements.filter((placement) => !movingIds.has(placement.id));
            const blocks: DropPreview["blocks"] = session.items.map((item) => ({
                placementId: item.placementId,
                startU: anchorStartU + item.relativeStartU,
                sizeU: item.sizeU,
            }));
            let reason: string | undefined;
            const previewPlacements: Placement[] = [];
            for (const [index, item] of session.items.entries()) {
                const block = blocks[index];
                if (block === undefined) {
                    continue;
                }
                const result = validatePlacement(
                    map,
                    [...provisional, ...previewPlacements],
                    item.componentId,
                    rack.id,
                    block.startU,
                );
                if (!result.valid) {
                    reason = result.reason ?? t("invalidPosition");
                    break;
                }
                previewPlacements.push({
                    id: `move-preview-${item.placementId}`,
                    componentId: item.componentId,
                    rackId: rack.id,
                    startU: block.startU,
                });
            }
            setPreview({
                blocks,
                valid: reason === undefined,
                ...(reason === undefined ? {} : { reason }),
            });
        } else {
            const ignoredId = session.kind === "placement" ? session.placementId : undefined;
            const result = validatePlacement(map, placements, session.componentId, rack.id, anchorStartU, ignoredId);
            setPreview({
                blocks: [{ startU: anchorStartU, sizeU: session.sizeU }],
                valid: result.valid,
                ...(result.reason === undefined ? {} : { reason: result.reason }),
            });
        }
        event.dataTransfer.dropEffect = session.kind === "catalog" ? "copy" : "move";
    }

    function completeDrop(event: React.DragEvent<HTMLDivElement>): void {
        event.preventDefault();
        const session = getDragSession();
        const target = preview;
        setPreview(null);
        setDragSession(null);
        if (session === null || target === null || !target.valid) {
            return;
        }
        const firstBlock = target.blocks[0];
        if (firstBlock === undefined) {
            return;
        }
        if (session.kind === "catalog") {
            onPlace(session.componentId, rack.id, firstBlock.startU);
        } else if (session.kind === "placement") {
            onMove(session.placementId, rack.id, firstBlock.startU);
        } else {
            onMoveMany(
                target.blocks.flatMap((block) =>
                    block.placementId === undefined
                        ? []
                        : [{ placementId: block.placementId, rackId: rack.id, startU: block.startU }],
                ),
            );
        }
    }

    return (
        <section
            className={`rack-designer${showColorOverlay ? " has-color-overlay" : ""}`}
            aria-labelledby="rack-designer-title"
        >
            <header className="rack-titlebar">
                <div className="rack-title-copy">
                    <span className="eyebrow">
                        {t("rackHeader")} · U{rack.sizeU}
                    </span>
                    <div className="rack-title-name-row">
                        <h2 id="rack-designer-title">{rackName}</h2>
                        <button
                            type="button"
                            className="rack-settings-button"
                            onClick={onEditRack}
                            aria-label={t("rackSettings")}
                            title={t("rackSettings")}
                        >
                            <SettingsIcon />
                        </button>
                    </div>
                </div>
                <div className="rack-title-actions">
                    <button
                        type="button"
                        className={`rack-label-toggle${showComponentLabels ? " is-active" : ""}`}
                        aria-pressed={showComponentLabels}
                        onClick={() => setShowComponentLabels((current) => !current)}
                    >
                        {t("labels")}
                    </button>
                    <button
                        type="button"
                        className={`rack-label-toggle${showColorOverlay ? " is-active" : ""}`}
                        aria-pressed={showColorOverlay}
                        onClick={() => setShowColorOverlay((current) => !current)}
                    >
                        {t("colorOverlay")}
                    </button>
                </div>
            </header>
            <div className="rack-workbench">
                <div className="rack-frame-wrap scroll-region" onScroll={updateMarqueeAfterScroll}>
                    <div className="rack-orientation">
                        <span>{t("top")}</span>
                        <i />
                        {rack.type === "wall-4u" ? t("wallRack") : mapName}
                    </div>
                    <div className="rack-selection-guide" aria-live="polite">
                        <span>
                            {selectedPlacementIds.length > 0
                                ? `${selectedPlacementIds.length} ${t("selected")}`
                                : t("multiSelectHint")}
                        </span>
                        {shortcutStatus === null ? null : <strong>{shortcutStatus}</strong>}
                    </div>
                    <div
                        ref={rackFrameRef}
                        className="rack-frame"
                        onPointerDown={beginMarquee}
                        onPointerMove={updateMarquee}
                        onPointerUp={finishMarquee}
                        onPointerCancel={finishMarquee}
                    >
                        <div className="rack-unit-numbers" aria-hidden="true">
                            {units.map((unit) => (
                                <span key={unit} style={{ height: rowHeight }}>
                                    {unit}
                                </span>
                            ))}
                        </div>
                        <div
                            className="rack-grid"
                            role="group"
                            aria-label={`${rackName} ${t("rackDropArea")}`}
                            style={{ height: rack.sizeU * rowHeight }}
                            onDragOver={updatePreview}
                            onDragLeave={(event) => {
                                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                                    setPreview(null);
                                }
                            }}
                            onDrop={completeDrop}
                        >
                            {units.map((unit) => (
                                <div key={unit} className="rack-unit-row" style={{ height: rowHeight }}>
                                    <span>{unit % 2 === 0 ? "•  •  •" : ""}</span>
                                </div>
                            ))}
                            {rack.fixedComponents?.map((fixed) => {
                                const definition = getComponent(fixed.componentId);
                                if (definition === undefined) {
                                    return null;
                                }
                                const componentName = entity("component", definition.id, definition.name);
                                const fixedName = entity("fixed", fixed.id, fixed.localName ?? componentName);
                                return (
                                    <div
                                        key={fixed.id}
                                        className="rack-component is-fixed"
                                        data-size-u={definition.sizeU}
                                        role="img"
                                        style={
                                            {
                                                ...getBlockPosition(
                                                    rack.sizeU,
                                                    fixed.startU,
                                                    definition.sizeU,
                                                    rowHeight,
                                                ),
                                                "--component-color": definition.color,
                                                "--component-image": `url("${definition.imagePath ?? ""}")`,
                                            } as React.CSSProperties
                                        }
                                        aria-label={`${fixedName}, ${t("fixedEquipment")}`}
                                    >
                                        <LockIcon />
                                        <span className="rack-component-labels">
                                            <strong className="rack-component-custom-name rack-component-name-badge">
                                                {fixedName}
                                            </strong>
                                            {showComponentLabels ? (
                                                <small className="rack-component-badge">
                                                    {componentName} · U{fixed.startU}
                                                </small>
                                            ) : null}
                                        </span>
                                        <em>{t("fixed")}</em>
                                    </div>
                                );
                            })}
                            {rackPlacements.map((placement) => {
                                const definition = getComponent(placement.componentId);
                                if (definition === undefined) {
                                    return null;
                                }
                                const componentName = entity("component", definition.id, definition.name);
                                const localDisplayName = getPlacementLocalDisplayName(
                                    placement.localName,
                                    rackName,
                                    showRackNameAsComponentPrefix,
                                );
                                return (
                                    <button
                                        key={placement.id}
                                        type="button"
                                        draggable
                                        data-placement-id={placement.id}
                                        data-size-u={definition.sizeU}
                                        className={`rack-component${selectedPlacementIds.includes(placement.id) ? " is-selected" : ""}`}
                                        style={
                                            {
                                                ...getBlockPosition(
                                                    rack.sizeU,
                                                    placement.startU,
                                                    definition.sizeU,
                                                    rowHeight,
                                                ),
                                                "--component-color": definition.color,
                                                "--component-image": `url("${definition.imagePath ?? ""}")`,
                                            } as React.CSSProperties
                                        }
                                        aria-pressed={selectedPlacementIds.includes(placement.id)}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            if (event.ctrlKey || event.metaKey) {
                                                setActionSelection(null);
                                                setSelectedPlacementIds((current) =>
                                                    current.includes(placement.id)
                                                        ? current.filter((placementId) => placementId !== placement.id)
                                                        : [...current, placement.id],
                                                );
                                                return;
                                            }
                                            const bounds = event.currentTarget.getBoundingClientRect();
                                            setSelectedPlacementIds([placement.id]);
                                            setActionSelection({
                                                placementId: placement.id,
                                                anchorLeft: bounds.left,
                                                anchorTop: bounds.top,
                                                anchorHeight: bounds.height,
                                            });
                                        }}
                                        onContextMenu={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            const bounds = event.currentTarget.getBoundingClientRect();
                                            setSelectedPlacementIds([placement.id]);
                                            setActionSelection({
                                                placementId: placement.id,
                                                anchorLeft: bounds.left,
                                                anchorTop: bounds.top,
                                                anchorHeight: bounds.height,
                                            });
                                        }}
                                        onDoubleClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            setActionSelection(null);
                                            setSelectedPlacementIds([placement.id]);
                                            onRename(placement);
                                        }}
                                        onDragStart={(event) => {
                                            const dragSelection = selectedPlacementIds.includes(placement.id)
                                                ? selectedPlacementIds
                                                : [placement.id];
                                            setSelectedPlacementIds(dragSelection);
                                            event.dataTransfer.effectAllowed = "move";
                                            event.dataTransfer.setData("application/x-uptime-placement", placement.id);
                                            const groupItems = rackPlacements.flatMap((item) => {
                                                if (!dragSelection.includes(item.id)) {
                                                    return [];
                                                }
                                                const itemDefinition = getComponent(item.componentId);
                                                return itemDefinition === undefined
                                                    ? []
                                                    : [
                                                          {
                                                              placementId: item.id,
                                                              componentId: item.componentId,
                                                              sizeU: itemDefinition.sizeU,
                                                              relativeStartU: item.startU - placement.startU,
                                                          },
                                                      ];
                                            });
                                            setDragSession(
                                                groupItems.length > 1
                                                    ? {
                                                          kind: "placement-group",
                                                          anchorPlacementId: placement.id,
                                                          anchorSizeU: definition.sizeU,
                                                          items: groupItems,
                                                      }
                                                    : {
                                                          kind: "placement",
                                                          placementId: placement.id,
                                                          componentId: placement.componentId,
                                                          sizeU: definition.sizeU,
                                                      },
                                            );
                                        }}
                                        onDragEnd={() => {
                                            setDragSession(null);
                                            setPreview(null);
                                        }}
                                    >
                                        <span className="rack-component-labels">
                                            {localDisplayName === undefined ? null : (
                                                <strong
                                                    className="rack-component-custom-name rack-component-name-badge"
                                                    title={localDisplayName}
                                                >
                                                    {localDisplayName}
                                                </strong>
                                            )}
                                            {showComponentLabels ? (
                                                <small className="rack-component-badge">
                                                    {componentName} · U{placement.startU}
                                                </small>
                                            ) : null}
                                        </span>
                                    </button>
                                );
                            })}
                            {preview?.blocks.map((block, index) => (
                                <div
                                    key={`${block.placementId ?? "new"}-${block.startU}-${index}`}
                                    className={`drop-preview ${preview.valid ? "is-valid" : "is-invalid"}`}
                                    style={getBlockPosition(rack.sizeU, block.startU, block.sizeU, rowHeight)}
                                >
                                    {index === 0 ? (
                                        <>
                                            <strong>
                                                {preview.valid
                                                    ? `${t("placeAt")} U${block.startU}`
                                                    : t("invalidPosition")}
                                            </strong>
                                            <small>
                                                {preview.valid
                                                    ? `${block.sizeU} ${block.sizeU === 1 ? t("rackUnit") : t("rackUnits")}`
                                                    : runtimeText(preview.reason)}
                                            </small>
                                        </>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                        <div className="rack-unit-numbers is-right" aria-hidden="true">
                            {units.map((unit) => (
                                <span key={unit} style={{ height: rowHeight }}>
                                    {unit}
                                </span>
                            ))}
                        </div>
                        {marquee === null ? null : (
                            <div
                                className="rack-selection-marquee"
                                aria-hidden="true"
                                style={{
                                    left: Math.min(marquee.startX, marquee.currentX),
                                    top: Math.min(marquee.startY, marquee.currentY),
                                    width: Math.abs(marquee.currentX - marquee.startX),
                                    height: Math.abs(marquee.currentY - marquee.startY),
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
            {actionSelection === null || selected === undefined || selectedDefinition === undefined
                ? null
                : createPortal(
                      <aside
                          className="component-action-popover"
                          role="dialog"
                          aria-label={`${t("actionsFor")} ${
                              selected.localName ?? entity("component", selectedDefinition.id, selectedDefinition.name)
                          }`}
                          style={
                              {
                                  top: Math.min(
                                      window.innerHeight - ACTION_POPOVER_HEIGHT,
                                      Math.max(
                                          8,
                                          actionSelection.anchorTop +
                                              actionSelection.anchorHeight / 2 -
                                              ACTION_POPOVER_HEIGHT / 2,
                                      ),
                                  ),
                                  left: Math.max(8, actionSelection.anchorLeft - ACTION_POPOVER_WIDTH - 8),
                                  width: ACTION_POPOVER_WIDTH,
                                  "--component-color": selectedDefinition.color,
                              } as React.CSSProperties
                          }
                      >
                          <header>
                              <div>
                                  <span className="eyebrow">{t("selectedEquipment")}</span>
                                  <h3>
                                      {selected.localName ??
                                          entity("component", selectedDefinition.id, selectedDefinition.name)}
                                  </h3>
                                  {selected.localName === undefined ? null : (
                                      <p>{entity("component", selectedDefinition.id, selectedDefinition.name)}</p>
                                  )}
                                  <small>
                                      {entity("category", selectedDefinition.category, selectedDefinition.category)} ·{" "}
                                      {selectedDefinition.sizeU}U · U{selected.startU}–U
                                      {selected.startU + selectedDefinition.sizeU - 1}
                                  </small>
                              </div>
                              <button
                                  type="button"
                                  className="popover-close"
                                  onClick={() => setActionSelection(null)}
                                  aria-label={t("closeActions")}
                              >
                                  ×
                              </button>
                          </header>
                          <div className="component-action-grid">
                              <button
                                  type="button"
                                  className="button"
                                  onClick={() => {
                                      setActionSelection(null);
                                      onRename(selected);
                                  }}
                              >
                                  <EditIcon />
                                  {t("localName")}
                              </button>
                              <button
                                  type="button"
                                  className="button button-danger"
                                  onClick={() => {
                                      setActionSelection(null);
                                      onDelete(selected.id);
                                  }}
                              >
                                  <TrashIcon />
                                  {t("remove")}
                              </button>
                          </div>
                      </aside>,
                      document.body,
                  )}
        </section>
    );
}
